import { useState, useEffect, useCallback } from 'react'
import type { MenuItem, ComboRow, ComboRowIngredient } from '@/types/menu'
import type { SelectedModifier } from '@/types/cart'
import type { LogicRule, RuleEvaluationResult, UserSelections } from '@/types/logicRules'
import type { ModifierGroupVariation, ModifierOption } from '@/types/modifier'
import type { RowVariation } from '@/types/menu'
import {
  evaluateRules,
  createEmptyRuleResult,
  getActiveVariation,
} from '@/utils/ruleEngine'

interface UseRuleEvaluationReturn {
  singleItemRuleResult: RuleEvaluationResult
  mainMealRuleResult: RuleEvaluationResult
  getGroupOptionsToDisplay: (group: {
    id: string
    options: ModifierOption[]
    variations?: ModifierGroupVariation[]
  }) => ModifierOption[]
  getRowIngredientsToDisplay: (row: ComboRow) => ComboRowIngredient[]
}

export function useRuleEvaluation(
  item: MenuItem | null,
  selectedModifiers: SelectedModifier[],
  mainMealCustomizations: SelectedModifier[],
  setSelectedModifiers: React.Dispatch<React.SetStateAction<SelectedModifier[]>>,
  setMainMealCustomizations: React.Dispatch<React.SetStateAction<SelectedModifier[]>>
): UseRuleEvaluationReturn {
  const [singleItemRuleResult, setSingleItemRuleResult] = useState<RuleEvaluationResult>(
    createEmptyRuleResult()
  )
  const [mainMealRuleResult, setMainMealRuleResult] = useState<RuleEvaluationResult>(
    createEmptyRuleResult()
  )

  // Build user selections from selected modifiers (for rule evaluation)
  const buildSingleItemSelections = useCallback((): UserSelections => {
    const selections: UserSelections = {}
    for (const mod of selectedModifiers) {
      const existing = selections[mod.groupId]
      if (existing) {
        if (Array.isArray(existing)) {
          existing.push({ id: mod.optionId, name: mod.optionName })
        } else {
          selections[mod.groupId] = [existing, { id: mod.optionId, name: mod.optionName }]
        }
      } else {
        selections[mod.groupId] = { id: mod.optionId, name: mod.optionName }
      }
    }
    return selections
  }, [selectedModifiers])

  // Build user selections from main meal customizations (for rule evaluation)
  const buildMainMealSelections = useCallback((): UserSelections => {
    const selections: UserSelections = {}
    for (const mod of mainMealCustomizations) {
      const existing = selections[mod.groupId]
      if (existing) {
        if (Array.isArray(existing)) {
          existing.push({ id: mod.optionId, name: mod.optionName })
        } else {
          selections[mod.groupId] = [existing, { id: mod.optionId, name: mod.optionName }]
        }
      } else {
        selections[mod.groupId] = { id: mod.optionId, name: mod.optionName }
      }
    }
    return selections
  }, [mainMealCustomizations])

  // Evaluate rules for single item when selections change
  useEffect(() => {
    if (!item || item.itemType !== 'single' || !item.logicRules?.length) {
      setSingleItemRuleResult(createEmptyRuleResult())
      return
    }

    const selections = buildSingleItemSelections()
    const rows =
      item.modifierGroups?.map((g) => ({
        id: g.id,
        can_be_hidden: g.canBeHidden,
      })) || []
    const result = evaluateRules(item.logicRules as LogicRule[], selections, rows)

    // Handle rows_to_clear effect
    if (result.rows_to_clear.size > 0) {
      setSelectedModifiers((prev) => {
        let updated = [...prev]
        for (const [rowId, keepIngredientId] of result.rows_to_clear) {
          if (keepIngredientId) {
            updated = updated.filter((m) => m.groupId !== rowId || m.optionId === keepIngredientId)
          } else {
            updated = updated.filter((m) => m.groupId !== rowId)
          }
        }
        return updated
      })
    }

    setSingleItemRuleResult(result)
  }, [item, buildSingleItemSelections, setSelectedModifiers])

  // Evaluate rules for combo main meal when selections change
  useEffect(() => {
    if (!item || item.itemType !== 'combo' || !item.comboProduct?.mainMeal?.logicRules?.length) {
      setMainMealRuleResult(createEmptyRuleResult())
      return
    }

    const selections = buildMainMealSelections()
    const rows =
      item.comboProduct.mainMeal.rows?.map((r) => ({
        id: r.id,
        can_be_hidden: r.canBeHidden,
      })) || []
    const result = evaluateRules(
      item.comboProduct.mainMeal.logicRules as LogicRule[],
      selections,
      rows
    )

    // Handle rows_to_clear effect
    if (result.rows_to_clear.size > 0) {
      setMainMealCustomizations((prev) => {
        let updated = [...prev]
        for (const [rowId, keepIngredientId] of result.rows_to_clear) {
          if (keepIngredientId) {
            updated = updated.filter((m) => m.groupId !== rowId || m.optionId === keepIngredientId)
          } else {
            updated = updated.filter((m) => m.groupId !== rowId)
          }
        }
        return updated
      })
    }

    setMainMealRuleResult(result)
  }, [item, buildMainMealSelections, setMainMealCustomizations])

  // Get the options to display for a modifier group based on active variation
  const getGroupOptionsToDisplay = useCallback(
    (group: {
      id: string
      options: ModifierOption[]
      variations?: ModifierGroupVariation[]
    }): ModifierOption[] => {
      const activeVariationId = getActiveVariation(group.id, singleItemRuleResult)

      if (activeVariationId && group.variations) {
        const activeVariation = group.variations.find((v) => v.id === activeVariationId)
        if (activeVariation && activeVariation.options.length > 0) {
          return activeVariation.options
        }
      }

      if (group.variations && group.variations.length > 0) {
        const defaultVariation = group.variations.find((v) => v.isDefault && v.options.length > 0)
        if (defaultVariation) {
          return defaultVariation.options
        }
      }

      return group.options
    },
    [singleItemRuleResult]
  )

  // Get the ingredients to display for a combo row based on active variation
  const getRowIngredientsToDisplay = useCallback(
    (row: {
      id: string
      ingredients: ComboRowIngredient[]
      variations?: RowVariation[]
    }): ComboRowIngredient[] => {
      const activeVariationId = getActiveVariation(row.id, mainMealRuleResult)

      if (activeVariationId && row.variations) {
        const activeVariation = row.variations.find((v) => v.id === activeVariationId)
        if (activeVariation && activeVariation.ingredients.length > 0) {
          return activeVariation.ingredients
        }
      }

      if (row.variations && row.variations.length > 0) {
        const defaultVariation = row.variations.find((v) => v.isDefault && v.ingredients.length > 0)
        if (defaultVariation) {
          return defaultVariation.ingredients
        }
      }

      return row.ingredients
    },
    [mainMealRuleResult]
  )

  // Clear selections when variation changes and ingredients are no longer available
  useEffect(() => {
    if (!item || item.itemType !== 'single') return

    const validSelections = selectedModifiers.filter((mod) => {
      // Ingredient variation selections are managed separately, not via modifierGroups
      if (item.ingredientVariationGroup && mod.groupId === item.ingredientVariationGroup.id) return true

      const group = item.modifierGroups?.find((g) => g.id === mod.groupId)
      if (!group) return false

      const displayOptions = getGroupOptionsToDisplay(group)
      return displayOptions.some((opt) => opt.id === mod.optionId)
    })

    if (validSelections.length !== selectedModifiers.length) {
      setSelectedModifiers(validSelections)
    }
  }, [item, singleItemRuleResult, getGroupOptionsToDisplay, selectedModifiers, setSelectedModifiers])

  // Clear main meal selections when variation changes
  useEffect(() => {
    if (!item || item.itemType !== 'combo' || !item.comboProduct?.mainMeal?.rows) return

    const validSelections = mainMealCustomizations.filter((mod) => {
      const row = item.comboProduct?.mainMeal?.rows.find((r) => r.id === mod.groupId)
      if (!row) return false

      const displayIngredients = getRowIngredientsToDisplay(row)
      return displayIngredients.some((ing) => ing.id === mod.optionId)
    })

    if (validSelections.length !== mainMealCustomizations.length) {
      setMainMealCustomizations(validSelections)
    }
  }, [
    item,
    mainMealRuleResult,
    getRowIngredientsToDisplay,
    mainMealCustomizations,
    setMainMealCustomizations,
  ])

  return {
    singleItemRuleResult,
    mainMealRuleResult,
    getGroupOptionsToDisplay,
    getRowIngredientsToDisplay,
  }
}
